import { cn } from "@istari-frontend/shared";
import { Button } from "@istari-frontend/ui";
import { Minimize2, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { SelectionAction } from "./file-viewer/selection-action";
import { ViewerPrefs } from "./file-viewer/url-fragment";

interface ImageViewerProps {
  src: string;
  alt: string;
  versionId?: string;
  initialView?: ViewerPrefs;
  onSelectionChange?: (action: SelectionAction) => void;
}

export function ImageViewer({ src, alt, versionId, initialView, onSelectionChange }: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number }>();
  const [containerSize, setContainerSize] = useState<{
    w: number;
    h: number;
  }>();
  const [scale, setScale] = useState<number>(1);
  const [translate, setTranslate] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [isPanning, setIsPanning] = useState(false);
  const scaleRef = useRef<number>(1);
  const translateRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const commitTimerRef = useRef<number | null>(null);

  const computeFitScale = useCallback(() => {
    if (!naturalSize) return 1;
    const currentContainerSize = containerSize || { w: 800, h: 600 };
    // Ensure container size has minimum dimensions
    const safeContainerSize = {
      w: Math.max(currentContainerSize.w, 100),
      h: Math.max(currentContainerSize.h, 100),
    };
    const pad = 32;
    const effectiveW = Math.max(safeContainerSize.w - 2 * pad, 1);
    const effectiveH = Math.max(safeContainerSize.h - 2 * pad, 1);
    const scale = Math.min(effectiveW / Math.max(1, naturalSize.w), effectiveH / Math.max(1, naturalSize.h));
    // Ensure scale is never 0 or negative
    return Math.max(scale, 0.01);
  }, [naturalSize, containerSize]);

  const centerAtScale = (targetScale: number) => {
    if (!naturalSize) return { x: 0, y: 0 };
    const rawContainerSize = containerSize || { w: 800, h: 600 };
    const safeContainerSize = {
      w: Math.max(rawContainerSize.w, 100),
      h: Math.max(rawContainerSize.h, 100),
    };
    const w = naturalSize.w * targetScale;
    const h = naturalSize.h * targetScale;
    return { x: (safeContainerSize.w - w) / 2, y: (safeContainerSize.h - h) / 2 };
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        // Ensure we have minimum dimensions to prevent scale calculation issues
        const newSize = {
          w: Math.max(cr.width, 100),
          h: Math.max(cr.height, 100),
        };

        setContainerSize(newSize);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const applyInitial = () => {
      const { naturalWidth, naturalHeight } = el;
      if (!naturalWidth || !naturalHeight) return;
      const newNaturalSize = { w: naturalWidth, h: naturalHeight };

      setNaturalSize(newNaturalSize);
    };
    if (el.complete) applyInitial();
    else el.onload = applyInitial;
  }, [src]);

  useEffect(() => {
    // Don't position until we have at least the natural size
    if (!naturalSize) return;

    // Use safe container size with minimum dimensions
    const rawContainerSize = containerSize || { w: 800, h: 600 };
    const safeContainerSize = {
      w: Math.max(rawContainerSize.w, 100),
      h: Math.max(rawContainerSize.h, 100),
    };

    // Apply initial view if it matches this version
    if (initialView && initialView.versionId === versionId) {
      const safeFitScale = computeFitScale();
      const s = safeFitScale * (initialView?.zoomRelativeToFit ?? 1);
      const centerPx = {
        x: (initialView?.centerNormalized?.x ?? 0.5) * naturalSize.w,
        y: (initialView?.centerNormalized?.y ?? 0.5) * naturalSize.h,
      };
      const tx = safeContainerSize.w / 2 - s * centerPx.x;
      const ty = safeContainerSize.h / 2 - s * centerPx.y;
      setScale(s);
      setTranslate({ x: tx, y: ty });
      scaleRef.current = s;
      translateRef.current = { x: tx, y: ty };
    } else {
      // Calculate fit scale and center position
      const safeFitScale = computeFitScale();
      const scaledW = naturalSize.w * safeFitScale;
      const scaledH = naturalSize.h * safeFitScale;
      const centeredTranslate = {
        x: (safeContainerSize.w - scaledW) / 2,
        y: (safeContainerSize.h - scaledH) / 2,
      };

      setScale(safeFitScale);
      setTranslate(centeredTranslate);
      scaleRef.current = safeFitScale;
      translateRef.current = centeredTranslate;
    }
  }, [naturalSize, containerSize, initialView, versionId, computeFitScale]);

  const handleZoomAtPoint = (multiplier: number, point?: { x: number; y: number }) => {
    if (!naturalSize) return;
    const rawContainerSize = containerSize || { w: 800, h: 600 };
    const safeContainerSize = {
      w: Math.max(rawContainerSize.w, 100),
      h: Math.max(rawContainerSize.h, 100),
    };
    const prevScale = scaleRef.current;
    let nextScale = prevScale * multiplier;
    nextScale = Math.max(0.05, Math.min(16, nextScale));
    const ratio = nextScale / prevScale;

    const targetPoint = point || { x: safeContainerSize.w / 2, y: safeContainerSize.h / 2 };
    const nextTranslate = {
      x: targetPoint.x - ratio * (targetPoint.x - translateRef.current.x),
      y: targetPoint.y - ratio * (targetPoint.y - translateRef.current.y),
    };
    setScale(nextScale);
    setTranslate(nextTranslate);
    scaleRef.current = nextScale;
    translateRef.current = nextTranslate;
  };

  // Attach non-passive wheel listener to properly prevent default page scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // In some browsers inside iframes, wheel events can be passive by default.

      if (e.cancelable) e.preventDefault();
      const delta = e.deltaY;
      const factor = Math.exp(-delta * 0.002);
      const rect = el.getBoundingClientRect();
      const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      handleZoomAtPoint(factor, point);
      if (commitTimerRef.current) window.clearTimeout(commitTimerRef.current);
      commitTimerRef.current = window.setTimeout(() => emitViewChange(), 120);
    };
    // Use capture to win over parent scroll handlers in the page shell
    el.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    // Prevent gesture-based zoom on Safari from bubbling/scrolling page
    const onGesture = (e: Event) => e.preventDefault();
    el.addEventListener("gesturestart", onGesture, { passive: false });
    el.addEventListener("gesturechange", onGesture, { passive: false });
    el.addEventListener("gestureend", onGesture, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel, true);
      el.removeEventListener("gesturestart", onGesture);
      el.removeEventListener("gesturechange", onGesture);
      el.removeEventListener("gestureend", onGesture);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Event listeners should only be set up once on mount
  }, []);

  const isPanningRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const translateStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const onPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    isPanningRef.current = true;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY };
    translateStartRef.current = translateRef.current;
  };
  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    const next = {
      x: translateStartRef.current.x + dx,
      y: translateStartRef.current.y + dy,
    };
    setTranslate(next);
    translateRef.current = next;
  };
  const onPointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    isPanningRef.current = false;
    setIsPanning(false);
    (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    emitViewChange();
  };

  const onDoubleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    handleZoomAtPoint(1.4, point);
    emitViewChange();
  };

  const emitViewChange = () => {
    if (!onSelectionChange || !naturalSize) return;
    const rawContainerSize = containerSize || { w: 800, h: 600 };
    const safeContainerSize = {
      w: Math.max(rawContainerSize.w, 100),
      h: Math.max(rawContainerSize.h, 100),
    };
    const fit = computeFitScale();
    const currentScale = scaleRef.current;
    const currentTranslate = translateRef.current;
    const zoomRelativeToFit = currentScale / (fit || 1);
    const centerPx = {
      x: (-currentTranslate.x + safeContainerSize.w / 2) / currentScale,
      y: (-currentTranslate.y + safeContainerSize.h / 2) / currentScale,
    };
    const centerNormalized = {
      x: Math.min(1, Math.max(0, centerPx.x / naturalSize.w)),
      y: Math.min(1, Math.max(0, centerPx.y / naturalSize.h)),
    };

    onSelectionChange({
      type: "image-view",
      centerNormalized,
      zoomRelativeToFit,
    });
  };

  const toFit = () => {
    setIsPanning(false);
    const s = computeFitScale();
    const centered = centerAtScale(s);
    setScale(s);
    setTranslate(centered);
    scaleRef.current = s;
    translateRef.current = centered;
    emitViewChange();
  };
  const fitScaleForDisplay = computeFitScale();
  const zoomPct = Math.round((scale / (fitScaleForDisplay || 1)) * 100);
  const canPan = true;

  return (
    <div
      ref={containerRef}
      className={cn(
        "bg-card relative h-full w-full overflow-hidden rounded-none select-none",
        "group/file-viewer bg-[radial-gradient(circle,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[length:15px_15px] dark:bg-[radial-gradient(circle,rgba(255,255,255,0.1)_1px,transparent_1px)]",
        canPan ? (isPanning ? "cursor-grabbing" : "cursor-grab") : "cursor-default",
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDoubleClick={onDoubleClick}
      style={{ touchAction: "none" }}
    >
      <div
        className="absolute top-0 left-0"
        style={{
          width: naturalSize ? `${naturalSize.w}px` : undefined,
          height: naturalSize ? `${naturalSize.h}px` : undefined,
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: "0 0",
          willChange: "transform",
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          className="block max-w-none"
          width={naturalSize?.w}
          height={naturalSize?.h}
        />
      </div>

      <div
        className={cn(
          "bg-card/80 pointer-events-none absolute bottom-3 left-1/2 z-20 flex items-center gap-1 rounded-xl border opacity-0 shadow-lg backdrop-blur-xl transition-opacity duration-200 group-focus-within/file-viewer:pointer-events-auto group-focus-within/file-viewer:opacity-100 group-hover/file-viewer:pointer-events-auto group-hover/file-viewer:opacity-100",
        )}
        style={{ transform: "translateX(-50%)" }}
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClickCapture={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="text-muted-foreground px-2 py-1 text-[0.625rem] font-medium">{zoomPct}%</div>
        <div className="bg-foreground/20 h-6 w-px" />
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:bg-card/80 h-7 w-7 p-0"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomAtPoint(1 / 1.1);
          }}
          title="Zoom out"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:bg-card/80 h-7 w-7 p-0"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomAtPoint(1.1);
          }}
          title="Zoom in"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        <div className="bg-foreground/20 h-6 w-px" />
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:bg-card/80 h-7 w-7 rounded-r-xl p-0"
          onClick={toFit}
          title="Zoom to fit (100%)"
        >
          <Minimize2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
